import { memo, useCallback } from 'react'
import { AlertTriangle, Eye, Sparkles, Wrench } from 'lucide-react'
import type { GridGap } from '../../lib/grid-gaps.js'
import { formatTime, formatDurationShort } from '../../lib/utils.js'
import { useServerClock } from '../../hooks/useServerClock.js'

interface GapBannerProps {
  gaps:    GridGap[]
  onFill:  (gap: GridGap, availableGaps: GridGap[]) => void
  onShow:  (gap: GridGap) => void
  /** Comblage automatique de tous les trous restants de la journée. */
  onFillAll: (availableGaps: GridGap[]) => void
}

/**
 * Bandeau de détection de trous, affiché seulement s'il y en a.
 *
 * Il annonce le premier trou de la journée. La fenêtre de remplissage reçoit
 * aussi la liste complète afin que l'opérateur puisse choisir lequel traiter.
 */
export const GapBanner = memo(function GapBanner({ gaps, onFill, onShow, onFillAll }: GapBannerProps) {
  const { nowMs } = useServerClock()
  const pendingGaps = gaps.filter(gap => new Date(gap.endTime).getTime() > nowMs)
  const gap = pendingGaps[0]
  const handleFill = useCallback(() => { if (gap) onFill(gap, pendingGaps) }, [gap, onFill, pendingGaps])
  const handleShow = useCallback(() => { if (gap) onShow(gap) }, [gap, onShow])
  const handleFillAll = useCallback(() => { if (pendingGaps.length) onFillAll(pendingGaps) }, [onFillAll, pendingGaps])

  if (!gap) return null

  return (
    <div className="agenda-gap-banner" role="status">
      <AlertTriangle size={14} aria-hidden="true" className="agenda-gap-banner__icon" />

      <span className="agenda-gap-banner__text">
        <strong>
          {pendingGaps.length === 1 ? '1 trou détecté' : `${pendingGaps.length} trous détectés`}
        </strong>
        {' — '}
        {formatTime(gap.startTime).slice(0, 5)} → {formatTime(gap.endTime).slice(0, 5)}
        {' ('}{formatDurationShort(gap.durationSec)}{') sans programme'}
      </span>

      {gap.nextItemTitle && (
        <span className="agenda-gap-banner__next">
          prochain point d’ancrage : {formatTime(gap.endTime).slice(0, 5)} {gap.nextItemTitle}
        </span>
      )}

      <span className="agenda-gap-banner__actions">
        <button
          type="button"
          className="tp-button tp-button--primary"
          onClick={handleFillAll}
          title="Appliquer une même règle de comblage à tous les trous de la journée"
        >
          <Sparkles size={13} aria-hidden="true" />
          Tout combler
        </button>
        <button type="button" className="tp-button" onClick={handleFill}>
          <Wrench size={13} aria-hidden="true" />
          Combler
        </button>
        <button type="button" className="tp-button" onClick={handleShow} title="Faire défiler la timeline jusqu’au trou">
          <Eye size={13} aria-hidden="true" />
          Voir
        </button>
      </span>
    </div>
  )
})
